import i18next from 'i18next';
import { authorizedFetch as fetch, fetchDELETE, fetchPOST, fetchPUT } from '@/helpers/authFetch';
import { NOSAMPLE } from '@/helpers/constants';
import { opensearchFilterBuilder } from '@/helpers/opensearch-utils';
import { getVersionUrlParam, isVansBeforeFlowRemoval, isRecipientsTab } from '@/helpers/utils';
import retryCampaignRequest from '@/utils/retryCampaignRequest';
import { campaignRequestEndpoint, checkFileOk, checkServiceOK } from './serviceConstants';

const isVansRecipients = (crType, tab, campaignDate) => isRecipientsTab(tab) && isVansBeforeFlowRemoval(crType, campaignDate);

const createNewCampaignRequest = (signal, campaign) => {
  // partCode, measure, requestTitle
  const lang = i18next.language.toUpperCase();
  const langTranslation = lang === 'EN' ? 'DE' : 'EN';
  const data = {
    // partCode,
    // typeOfErrorCode: measure,
    partCode: '82A06',
    typeOfErrorCode: 'measure',
    selectedModelSeries: '',
  };

  if (campaign.finNumber !== '') {
    data.selectedModelSeries = `C${campaign.finNumber.slice(0, 3)}`;
  } else if (campaign.model !== '') {
    data.selectedModelSeries = campaign.model;
  }

  const measureCases = () => {
    let title = {
      [lang]: campaign.requestTitle,
      [langTranslation]: campaign.requestTitleTranslation,
    };

    if (campaign.measure) {
      title = {
        [lang]: `${campaign.requestTitle}, ${campaign.measure}`,
        [langTranslation]: `${campaign.requestTitleTranslation}, ${campaign.measure}`,
      };
    } else if (campaign.requestTitleTranslation) {
      title = {
        [lang]: campaign.requestTitle,
        [langTranslation]: campaign.requestTitleTranslation,
      };
    }

    return title;
  };

  if (campaign.entries !== NOSAMPLE) {
    data.title = measureCases();
  } else {
    data.title = {
      [lang]: campaign.measure
        ? `${campaign.requestTitle}, ${campaign.measure}`
        : campaign.requestTitle,
      [langTranslation]: '',
    };
  }

  return fetchPOST(`${campaignRequestEndpoint}/campaigns`, data, signal, { 'Accept-Language': lang })
    .then((res) => checkServiceOK(res, true));
};

const updateCampaignTitle = (id, titleData) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}`, titleData)
    .then((res) => checkServiceOK(res, true))
);

// For campaign "info" use tab = ''
const getCampaign = (signal, id, tab = '', version) => (tab === ''
  ? fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}`, { signal }).then(checkServiceOK)
  : fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/${tab}`, { signal }).then(checkServiceOK));

const getCampaignRetry = (id, tab = '', version, crType, campaignDate) => {
  const isTypeVans = isVansRecipients(crType, tab, campaignDate);

  return retryCampaignRequest(tab === 'workflows'
    ? `${campaignRequestEndpoint}/${tab}/${id}${getVersionUrlParam(version)}/true`
    : `${campaignRequestEndpoint}/${isTypeVans ? 'vans-' : ''}campaigns/${id}${getVersionUrlParam(version)}/${tab}`);
};

const getCampaignRetryWithSignal = (signal, id, tab = '', version) => retryCampaignRequest(tab === 'workflows'
  ? `${campaignRequestEndpoint}/${tab}/${id}${getVersionUrlParam(version)}/true`
  : `${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/${tab}`, { signal });

const getCampaignRetryV2 = (id, tab = '', version) => retryCampaignRequest(tab === 'workflows'
  ? `${campaignRequestEndpoint}/${tab}/${id}${getVersionUrlParam(version)}/true`
  : `${campaignRequestEndpoint}/campaigns/v2/${id}${getVersionUrlParam(version)}/${tab}`);

const getCampaignRiskSCERelevancy = (signal, id, version) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/classification/potential-SCE`, { signal }).then(checkServiceOK)
);

const deleteCampaign = (id) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}`, { method: 'DELETE' })
    .then((res) => checkServiceOK(res, true))
);

const saveCampaign = (signal, id, data, tab, crType, campaignDate) => {
  const isTypeVans = isVansRecipients(crType, tab, campaignDate);

  return fetchPUT(`${campaignRequestEndpoint}/${isTypeVans ? 'vans-' : ''}campaigns/${id}/${tab}`, data, signal).then((res) => checkServiceOK(res, true));
};

const updateCampaignRecipients = (campaignId, data, field, crType, campaignDate) => {
  const isVans = isVansBeforeFlowRemoval(crType, campaignDate);

  return fetchPUT(`${campaignRequestEndpoint}/${isVans ? 'vans-' : ''}campaigns/${campaignId}/recipients/${field}`, data).then((res) => checkServiceOK(res, true));
};

const saveApprovalsSkip = (campaignId, data) => (
  fetchPUT(`${campaignRequestEndpoint}/workflows/${campaignId}/approval-change`, data).then((res) => checkServiceOK(res, true))
);

const getTabForms = (tabCode, campaignDate) => (
  fetch(`${campaignRequestEndpoint}/forms/${tabCode}${campaignDate ? `/${campaignDate}` : ''}`).then(checkServiceOK)
);
const getTabFormsWithSignal = (signal, tabCode, campaignDate) => (
  fetch(`${campaignRequestEndpoint}/forms/${tabCode}${campaignDate ? `/${campaignDate}` : ''}`, { signal }).then(checkServiceOK)
);

// ---------- Dashboard --------------------------------------

const getCampaignsStatistics = (chips, isTopLevel = false) => {
  const stringChips = chips?.join('&');

  return fetch(`${campaignRequestEndpoint}/campaigns/counter?${stringChips}&isTopLevel=${isTopLevel}`).then(checkServiceOK);
};

const getNews = (signal) => fetch(`${campaignRequestEndpoint}/news`, { signal }).then(checkServiceOK);

const getTasks = (chips, isTopLevel = false) => {
  const stringChips = chips?.join('&');

  return fetch(`${campaignRequestEndpoint}/campaigns/tasks?${stringChips}&isTopLevel=${isTopLevel}`).then(checkServiceOK);
};
// ---------- SUMMARY ------------------------------------

const getSummaryForms = () => (
  fetch(`${campaignRequestEndpoint}/forms/EXECUTIVE_SUMMARY`).then(checkServiceOK)
);
// ---------- Affected Software ------------------------------

const getAffectedSoftware = (id) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}/affectedSoftware`).then(checkServiceOK)
);

// ---------- Workflow ------------------------------

const getWorkflow = (id, version) => (
  fetch(`${campaignRequestEndpoint}/workflows/${id}${getVersionUrlParam(version)}/false`).then(checkServiceOK)
);

// ------------ Attachments ----------------------------------

const getAttachments = (id) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}/attachments`).then(checkServiceOK)
);

const deleteAttachment = (attachmentId, CR) => (
  fetchDELETE(`${campaignRequestEndpoint}/campaigns/${CR}/attachments?attachmentId=${encodeURIComponent(attachmentId)}`)
    .then((res) => checkServiceOK(res, true))
);

const deleteSubCampaignAttachment = (attachmentId, subCampaignId) => (
  fetchDELETE(`${campaignRequestEndpoint}/sub-campaigns/${subCampaignId}/attachments?attachmentId=${encodeURIComponent(attachmentId)}`)
    .then((res) => checkServiceOK(res, true))
);

const deleteAttachmentPlant = (restrictionFileId, CR, wkzNumber) => (
  fetchDELETE(`${campaignRequestEndpoint}/campaigns/${CR}/${wkzNumber}/restriction-file?restrictionFileId=${encodeURIComponent(restrictionFileId)}`)
    .then((res) => checkServiceOK(res, true))
);

const setFavorite = (attachmentId, CR) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${CR}/attachments/set-favorite?attachmentId=${encodeURIComponent(attachmentId)}`, attachmentId)
    .then((res) => checkServiceOK(res, true))
);

const updateAttachmentJvVisibility = (attachmentId, CR, jvVisible, tag) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${CR}/attachments/update?attachmentId=${encodeURIComponent(attachmentId)}&jointVenture=${jvVisible}&tag=${tag}`, {})
    .then((res) => checkServiceOK(res, true))
);

const updateSubCampaignAttachment = (attachmentId, subCampaignId, jvVisible, tag) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${subCampaignId}/attachments/update?attachmentId=${encodeURIComponent(attachmentId)}&jointVenture=${jvVisible}&tag=${tag}`, {})
    .then((res) => checkServiceOK(res, true))
);

// ----------------------------------------

const getHelps = (tab) => (
  fetch(`${campaignRequestEndpoint}/helps/${tab}`).then(checkServiceOK)
);

const getCampaignRequests = () => (
  fetch(`${campaignRequestEndpoint}/campaigns`).then(checkServiceOK)
);

const getCampaignRequestsWithOpenSearch = (signal, chip, data = opensearchFilterBuilder, page = 0, pageSize = 10, sort = 'campaigns.creationDate', sortDir = CAPITAL_DESC, childSort = undefined, childSortDir, lang) => (
  fetchPOST(`${campaignRequestEndpoint}/campaigns/filtered?chipFilter=${chip}&page=${page}&size=${pageSize}&sort=${sort},${sortDir}${lang ? `,${lang}` : ''}${childSort ? `&childSort=${childSort},${childSortDir}` : ''}`, data, signal).then(checkServiceOK));

const getSubCampaignRequests = () => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns`).then(checkServiceOK)
);

const getSubCampaignFeedbackForms = () => (
  fetch(`${campaignRequestEndpoint}/forms/sub_campaign_feedback`).then(checkServiceOK)
);

const getSubCampaignInfo = (signal, id) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${id}`, { signal }).then(checkServiceOK)
);

const putFeedback = (signal, id, data) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/modules/feedback`, data, signal)
    .then((res) => checkServiceOK(res, true))
);

const createZASMCampaign = (signal, subCRId) => (
  fetchPOST(`${campaignRequestEndpoint}/sub-campaigns/${subCRId}/modules/feedback/zasm-campaign`, {}, signal)
    .then((res) => checkServiceOK(res, true))
);

const linkCampaignToZASM = (signal, data) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${data.cartId}/modules/feedback/zasm-campaign-sync`, data, signal)
    .then((res) => checkServiceOK(res, true))
);

const putAIQualityFlag = (signal, subCRId, version, value) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${subCRId}/${version}/ai-quality`, { aiQuality: value }, signal)
    .then((res) => checkServiceOK(res, true))
);

const advanceNextStatusSubCampaign = (id, data, isAccepted = false) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/next-status${isAccepted ? '-accepted' : ''}`, data)
    .then((res) => checkServiceOK(res, true))
);

const saveSubCampaign = (id, tab, data) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/${tab}`, data).then((res) => checkServiceOK(res, true))
);

const saveComments = (signal, id, tab, data) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/modules/${tab}/notes`, data, signal)
    .then((res) => checkServiceOK(res, true)));

const deleteCampaignsToRecycleBin = (signal, campaigns) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/recycle-bin`, campaigns, signal)
    .then((res) => checkServiceOK(res, true)));

const deleteCampaignInRecycleBin = (campaigns) => (
  fetchDELETE(`${campaignRequestEndpoint}/campaigns/recycle-bin`, campaigns)
    .then((res) => checkServiceOK(res, true)));

const moveToNextStatus = (id, data) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/next-status`, data)
    .then((res) => checkServiceOK(res, true)));

const copyCampaignRequest = (id) => (
  fetchPOST(`${campaignRequestEndpoint}/campaigns/${id}/copy`)
    .then((res) => checkServiceOK(res, true)));

const copyPlant = (id, wkzNumber, data) => (
  fetchPOST(`${campaignRequestEndpoint}/campaigns/${id}/${wkzNumber}/limitation-volume`, data)
    .then((res) => checkServiceOK(res, false)));

const changeDecision = (id, data, isSubCR = false) => (
  fetchPUT(`${campaignRequestEndpoint}/${isSubCR ? 'sub-' : ''}campaigns/${id}/change`, data)
    .then((res) => checkServiceOK(res, true)));

const rejectDecision = (id, data) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/reject`, data)
    .then((res) => checkServiceOK(res, true)));

const plantNotAffected = (id, data) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/plant-not-affected`, data)
    .then((res) => checkServiceOK(res, true)));

const closeCampaign = (id, data) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/close`, data)
    .then((res) => checkServiceOK(res, true)));

const closeWorkflowDialog = (id, statusCode, isSubCR = false) => (
  fetchPUT(`${campaignRequestEndpoint}/${isSubCR ? 'sub-campaign-workflows' : 'workflows'}/${id}/${statusCode}/seen-by`)
    .then((res) => checkServiceOK(res, true)));

const getSubCampaign = (id, tab, version) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${id}${getVersionUrlParam(version)}/modules/${tab}`).then(checkServiceOK)
);

const getSubCampaignWithSignal = (signal, id, tab, version) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${id}${getVersionUrlParam(version)}/modules/${tab}`, { signal }).then(checkServiceOK)
);

const getSubCampaignComments = (signal, id, version) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${id}${getVersionUrlParam(version)}/notes`, { signal }).then(checkServiceOK)
);

const getPlantsLanguages = (wkzs) => (
  fetch(`${campaignRequestEndpoint}/plants/${wkzs}/languages`).then(checkServiceOK)
);

const getIsCampaignWithoutChanges = (id) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}/requestWithoutChanges`).then(checkServiceOK)
);

const getCampaignPlantFeedback = (id, version) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/plant-feedback`).then(checkServiceOK)
);

const getSubCampaignPlantFeedback = (id, plant, version) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/plant-feedback?plant=${plant}`).then(checkServiceOK)
);

const getVehiclesStatus = (id, version) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/plant-feedback-overview`).then(checkServiceOK)
);

const getAffectedPlants = (signal, id, version = 1, plantNumbers) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}${getVersionUrlParam(version)}/affected-plants?${plantNumbers.map((p) => `plants=${p}`).join('&')}`, { signal }).then(checkServiceOK)
);

const getPlantName = (wkzNumber) => (
  fetch(`${campaignRequestEndpoint}/plants/${wkzNumber}`).then(checkServiceOK)
);

const putAssignCR = (id, data, crType, campaignDate) => {
  const isVans = isVansBeforeFlowRemoval(crType, campaignDate);

  return fetchPUT(`${campaignRequestEndpoint}/${isVans ? 'vans-' : ''}campaigns/${id}/assign`, data)
    .then((res) => checkServiceOK(res, true));
};

const putAssignCreatorCR = (id, newCreator) => (fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/assignCreator?userCreator=${newCreator}`, {})
  .then((res) => checkServiceOK(res, true))
);

const putAssignSubCR = (id, userCode) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/assign?userCode=${userCode}`)
    .then((res) => checkServiceOK(res, true))
);

const getTranslations = (id) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}/translations`).then(checkServiceOK)
);

const getAutomaticTranslations = (id, updateOnlyEmptyFields = false) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/automatic-translations?updateOnlyEmptyFields=${updateOnlyEmptyFields}`, {})
    .then((res) => checkServiceOK(res, true))
);

const getSubCRWorkflow = (id) => (
  fetch(`${campaignRequestEndpoint}/sub-campaign-workflows/${id}/true`).then(checkServiceOK)
);

const getAdaptedSubCRWorkflow = (subCRId, version) => (
  fetch(`${campaignRequestEndpoint}/sub-campaign-workflows-general/${subCRId}/${version}`).then(checkServiceOK)
);

const avanceAllSubCRsOfCRToNextStatus = (crId, data = {}, fromInConsolidation = false) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${crId}/next-status-by-campaign-id${fromInConsolidation ? '-consolidation' : ''}`, data)
    .then((res) => checkServiceOK(res, true))
);

const changeDecisionAllSubCRsOfCR = (crId, data = {}) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${crId}/change-by-campaign-id`, data)
    .then((res) => checkServiceOK(res, true)));

const rejectSubCampaign = (id, data) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/${id}/reject`, data)
    .then((res) => checkServiceOK(res, true)));

const getStatuses = (isSubCR) => (
  fetch(`${campaignRequestEndpoint}/${isSubCR ? 'sub-' : ''}campaigns/statuses`).then(checkServiceOK)
);

const getCampaignChanges = (signal, id, previousVersion, currentVersion) => (
  fetch(`${campaignRequestEndpoint}/campaigns/v2/${id}/compare/${currentVersion}/${previousVersion}`, { signal }).then(checkServiceOK)
);

const checkSubCampaignRecipients = (id) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/can-see?campaignId=${id}`).then(checkServiceOK)
);

const getCROverviewResults = (signal, chipFilter, filter = opensearchFilterBuilder, sort = 'campaigns.creationDate', sortDir = CAPITAL_DESC, columns, lang = 'EN') => (
  fetchPOST(`${campaignRequestEndpoint}/campaigns-export/excel?chipFilter=${chipFilter}&sort=${sort},${sortDir}&columns=${columns}&language=${lang}`, filter, signal)
    .then(checkFileOk));

const getCampaignDocumentsList = (signal, page, size, sort) => (
  fetch(`${campaignRequestEndpoint}/campaigns/byUserPlant?page=${page}&size=${size}&sort=${sort}`, signal)
    .then(checkServiceOK));

const getDialogInformation = (signal, partNumber, langCode) => (
  fetch(`${campaignRequestEndpoint}/dialog/part/${partNumber}?languageCode=${langCode}`, { signal }).then(checkServiceOK)
);

const checkE2SignatureActive = (signal, campaignId) => (
  fetch(`${campaignRequestEndpoint}/vans-campaigns/${campaignId}/subcampaigns-approved`, { signal }).then(checkServiceOK)
);

const checkIsAssignable = (signal, subCRId) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${subCRId}/isAssignable`, { signal }).then(checkServiceOK)
);

const checkZASMCampaignExists = (signal, campaignNumber) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/feedback/check-zasm-campaign-exists/${campaignNumber}`, { signal }).then(checkServiceOK)
);

const getSbmStatus = (doormanBundleId) => (
  fetch(`${campaignRequestEndpoint}/campaigns/sbm-status/${doormanBundleId}`).then(checkServiceOK)
);

const getHighligtsOnSearch = (id, isTopLevel, searchTerm) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}/highlights?isTopLevel=${isTopLevel}&searchTerm=${searchTerm}`).then(checkServiceOK)
);

const canApproveSubCampaign = (subCRId) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${subCRId}/can-approve`).then(checkServiceOK)
);

const getStatusSummaryDashboard = (signal, filterDays, groupingLevels) => (
  fetch(`${campaignRequestEndpoint}/campaigns/status-summary?filterDays=${filterDays}${groupingLevels ? `&groupingLevels=${groupingLevels}` : ''}`, signal)
    .then(checkServiceOK)
);

const getStatusSummaryTableDashboard = (signal, crStatus, subCRStatus, groupBy, groupByValue, page, size, sort) => (
  fetch(`${campaignRequestEndpoint}/campaigns/status-summary/detail?status=${crStatus}${subCRStatus ? `&subStatus=${subCRStatus}` : ''}${groupBy ? `&${groupBy}=${groupByValue}` : ''}&page=${page}&size=${size}&sort=${sort}`, signal)
    .then(checkServiceOK)
);

const chekCMDecisions = (subCRId, version) => (
  fetch(`${campaignRequestEndpoint}/sub-campaign-workflows-general/by-main/${subCRId}/${version}`).then(checkServiceOK)
);

const splitSubCampaign = (signal, subCRId, data) => (
  fetchPOST(`${campaignRequestEndpoint}/sub-campaigns/${subCRId}/split`, data, signal)
    .then((res) => checkServiceOK(res, true))
);

const getAssignments = (id) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${id}/assignments`).then(checkServiceOK)
);

const markAssignments = (id, partIds) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/assignments/parts/mark-as-assigned`, partIds).then((res) => checkServiceOK(res, true)));

const unmarkAssignments = (id, partIds) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${id}/assignments/parts/mark-as-unassigned`, partIds).then((res) => checkServiceOK(res, true)));

const getAffectedVehicles = (id) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${id}/affected-vehicles`).then(checkServiceOK)
);

const updateAffectedVehicles = (updatedData) => (
  fetchPUT(`${campaignRequestEndpoint}/sub-campaigns/update-split`, updatedData).then((res) => checkServiceOK(res, true))
);

const prefillCampaignWithAttachments = (crId, data) => (
  fetchPOST(`${campaignRequestEndpoint}/campaigns/${crId}/split`, data)
    .then((res) => checkServiceOK(res, true))
);

const getMbosPartNumberInfo = (oldPartNumber, oldYWP) => {
  const encodedYWP = encodeURIComponent(oldYWP);

  return fetch(`${campaignRequestEndpoint}/mbos/part/${oldPartNumber}?iwpVersion=${encodedYWP}`).then(checkServiceOK);
};

const checkOldNumberExist = (oldPartNumber) => (
  fetch(`${campaignRequestEndpoint}/mbos/part/${oldPartNumber}/exists`).then(checkServiceOK)
);

const checkUuidCode = (uuidNumber) => (
  fetch(`${campaignRequestEndpoint}/mbos/uuid/${uuidNumber}/exists`).then(checkServiceOK)
);

const saveSelectedFields = (crId, selectedFields) => (
  fetchPUT(`${campaignRequestEndpoint}/campaigns/${crId}/save-selection`, selectedFields).then((res) => checkServiceOK(res, true))
);

const getSelectedFields = (crId, version) => (
  fetch(`${campaignRequestEndpoint}/campaigns/${crId}/${version}/highlight-fields`).then(checkServiceOK)
);

const getSubCampaignHistory = (signal, subCRId) => (
  fetch(`${campaignRequestEndpoint}/sub-campaigns/${subCRId}/history`, { signal }).then(checkServiceOK)
);

export default {
  createNewCampaignRequest,
  updateCampaignTitle,
  getCampaign,
  getCampaignRetry,
  getCampaignRetryWithSignal,
  getCampaignRetryV2,
  getCampaignRiskSCERelevancy,
  deleteCampaign,
  saveCampaign,
  updateCampaignRecipients,
  saveApprovalsSkip,
  getCampaignsStatistics,
  getTabForms,
  getTabFormsWithSignal,
  getSummaryForms,
  getAffectedSoftware,
  getAttachments,
  deleteAttachment,
  deleteSubCampaignAttachment,
  deleteAttachmentPlant,
  setFavorite,
  updateAttachmentJvVisibility,
  updateSubCampaignAttachment,
  getHelps,
  getCampaignRequests,
  getCampaignRequestsWithOpenSearch,
  deleteCampaignsToRecycleBin,
  deleteCampaignInRecycleBin,
  moveToNextStatus,
  copyCampaignRequest,
  changeDecision,
  rejectDecision,
  getWorkflow,
  closeCampaign,
  getNews,
  copyPlant,
  closeWorkflowDialog,
  getSubCampaignRequests,
  getSubCampaignFeedbackForms,
  saveSubCampaign,
  advanceNextStatusSubCampaign,
  putAssignSubCR,
  getSubCampaign,
  getSubCampaignWithSignal,
  getSubCampaignComments,
  saveComments,
  getPlantsLanguages,
  putFeedback,
  createZASMCampaign,
  getSubCampaignInfo,
  getIsCampaignWithoutChanges,
  getCampaignPlantFeedback,
  getSubCampaignPlantFeedback,
  getAffectedPlants,
  getPlantName,
  putAssignCR,
  putAssignCreatorCR,
  getTranslations,
  getAutomaticTranslations,
  getSubCRWorkflow,
  getAdaptedSubCRWorkflow,
  rejectSubCampaign,
  getStatuses,
  getCampaignChanges,
  getCROverviewResults,
  avanceAllSubCRsOfCRToNextStatus,
  changeDecisionAllSubCRsOfCR,
  getCampaignDocumentsList,
  getDialogInformation,
  checkE2SignatureActive,
  checkIsAssignable,
  checkZASMCampaignExists,
  checkSubCampaignRecipients,
  getTasks,
  getHighligtsOnSearch,
  canApproveSubCampaign,
  getSbmStatus,
  getStatusSummaryDashboard,
  getStatusSummaryTableDashboard,
  chekCMDecisions,
  splitSubCampaign,
  linkCampaignToZASM,
  getAssignments,
  markAssignments,
  unmarkAssignments,
  getAffectedVehicles,
  updateAffectedVehicles,
  prefillCampaignWithAttachments,
  getMbosPartNumberInfo,
  saveSelectedFields,
  getSelectedFields,
  getSubCampaignHistory,
  checkOldNumberExist,
  checkUuidCode,
  getVehiclesStatus,
  plantNotAffected,
  putAIQualityFlag,
};
